import React from 'react';
import { Users, Loader2, User } from 'lucide-react';
import { useClassmates } from '../../hooks/useStudent';

interface ClassmatesTabProps {
  groupeId: string;
}

const ClassmatesTab: React.FC<ClassmatesTabProps> = ({ groupeId }) => {
  const { data: classmates = [], isLoading, error } = useClassmates(groupeId);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
        Erreur lors du chargement des camarades
      </div>
    );
  }

  if (classmates.length === 0) {
    return (
      <div className="text-center py-12">
        <Users className="mx-auto h-12 w-12 text-gray-400" />
        <h3 className="mt-4 text-lg font-medium text-gray-900">Aucun camarade</h3>
        <p className="mt-2 text-sm text-gray-500">
          Vous êtes le seul étudiant inscrit dans ce groupe.
        </p>
      </div>
    );
  }

  // Get initials from name
  const getInitials = (firstName: string, lastName: string) => {
    return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
  };

  // Generate a consistent color for each student
  const getAvatarColor = (id: string) => {
    const colors = [
      'bg-blue-500',
      'bg-green-500',
      'bg-purple-500',
      'bg-pink-500',
      'bg-indigo-500',
      'bg-teal-500',
      'bg-orange-500',
      'bg-cyan-500',
    ];
    const index = id.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
    return colors[index % colors.length];
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
          <Users className="h-5 w-5 text-blue-600" />
          Mes Camarades
        </h2>
        <span className="text-sm text-gray-500">
          {classmates.length} étudiant{classmates.length !== 1 ? 's' : ''}
        </span>
      </div>

      {/* Classmates Grid */}
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
        {classmates.map((classmate) => (
          <div
            key={classmate.id}
            className="bg-white rounded-lg border border-gray-200 p-4 text-center hover:shadow-md transition-shadow"
          >
            {/* Avatar */}
            {classmate.photoUrl ? (
              <img
                src={classmate.photoUrl}
                alt={`${classmate.firstName} ${classmate.lastName}`}
                className="w-16 h-16 rounded-full mx-auto object-cover"
              />
            ) : (
              <div
                className={`w-16 h-16 rounded-full mx-auto flex items-center justify-center text-white text-lg font-medium ${getAvatarColor(classmate.id)}`}
              >
                {getInitials(classmate.firstName, classmate.lastName)}
              </div>
            )}

            {/* Name */}
            <div className="mt-3">
              <p className="font-medium text-gray-900 truncate">
                {classmate.firstName}
              </p>
              <p className="text-sm text-gray-500 truncate">
                {classmate.lastName}
              </p>
            </div>
          </div>
        ))}
      </div>

      {/* Privacy note */}
      <div className="bg-gray-50 border border-gray-200 text-gray-600 px-4 py-3 rounded-lg text-sm flex items-start gap-2">
        <User className="h-5 w-5 text-gray-400 flex-shrink-0 mt-0.5" />
        <p>
          Pour des raisons de confidentialité, seuls les noms et photos de profil de vos camarades sont affichés.
        </p>
      </div>
    </div>
  );
};

export default ClassmatesTab;
